import moment from 'moment'
import { defineStore } from 'pinia'

import api from '@/api/api'
import { ReservationCreateResponse } from '@/api/manual_types'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'

import {
  CouponId,
  CouponRead,
  PerformanceReservationsRead,
  ReservationId,
  ReservationStatus,
  StripeReaderId,
  StripeReaderRead,
} from '@generated/types'

export const TERMINAL_PAYMENT_STATE = {
  NONE: 'none',
  PAYING: 'paying',
  RETRY: 'retry',
}
export type TerminalPaymentState =
  (typeof TERMINAL_PAYMENT_STATE)[keyof typeof TERMINAL_PAYMENT_STATE]

export interface TerminalState {
  readers: StripeReaderRead[]
  selectedReaderId: StripeReaderId | null
  terminalPaymentState: TerminalPaymentState
}

export type DeclineCodeType =
  | 'generic_decline'
  | 'insufficient_funds'
  | 'lost_card'
  | 'stolen_card'
  | 'expired_card'
  | 'incorrect_cvc'
  | 'processing_error'
  | 'incorrect_number'
export const DeclineCode: Record<string, DeclineCodeType> = {
  genericDecline: 'generic_decline',
  insufficientFunds: 'insufficient_funds',
  lostCard: 'lost_card',
  stolenCard: 'stolen_card',
  expiredCard: 'expired_card',
  incorrectCvc: 'incorrect_cvc',
  processingError: 'processing_error',
  incorrectNumber: 'incorrect_number',
}
const CARD_NUMBERS: Record<DeclineCodeType, string> = {
  generic_decline: '4000000000000002',
  insufficient_funds: '4000000000009995',
  incorrect_cvc: '4000000000000127',
  expired_card: '4000000000000069',
  processing_error: '4000000000000119',
  incorrect_number: '4242424242424241',
  lost_card: '4000000000009979',
  stolen_card: '4000000000009987',
}

export const useTerminalStore = defineStore({
  id: 'terminal',
  persist: true,
  state: (): TerminalState => {
    return {
      readers: [],
      selectedReaderId: null,
      terminalPaymentState: TERMINAL_PAYMENT_STATE.NONE,
    }
  },
  actions: {
    async fetchReaders(): Promise<void> {
      const response = await api.getReaders()
      this.readers = response.data
    },
    setSelectedReaderId(reader: StripeReaderId | null): void {
      this.selectedReaderId = reader
    },
    async cancelReaderAction(): Promise<void> {
      if (!this.selectedReader) {
        return
      }
      if (this.isPayingInTerminal) await api.cancelReaderAction(this.selectedReader.id)
      this.setTerminalPaymentStateNone()
    },
    async simulateSuccess(): Promise<void> {
      if (!this.selectedReader) {
        return
      }
      await api.simulatePayment(this.selectedReader.id, '4242424242424242')
    },
    async simulateDecline(declineCode: DeclineCodeType): Promise<void> {
      if (!this.selectedReader) {
        return
      }
      await api.simulatePayment(this.selectedReader.id, CARD_NUMBERS[declineCode])
    },
    setTerminalPaymentState(state: TerminalPaymentState) {
      this.terminalPaymentState = state
    },
    setTerminalPaymentStateNone() {
      this.setTerminalPaymentState(TERMINAL_PAYMENT_STATE.NONE)
    },
    setTerminalPaymentStatePaying() {
      this.setTerminalPaymentState(TERMINAL_PAYMENT_STATE.PAYING)
    },
    setTerminalPaymentStateRetry() {
      this.setTerminalPaymentState(TERMINAL_PAYMENT_STATE.RETRY)
    },
    async sellWalkIn(
      performance: PerformanceReservationsRead,
      tableIds: number[],
      numberOfGuests: number,
      amount: string,
      coupon_id?: CouponId,
    ): Promise<ReservationCreateResponse> {
      try {
        const reservationStore = useReservationStore()
        this.setTerminalPaymentStatePaying()
        const result = await reservationStore.createReservation({
          status: ReservationStatus.UNPAID,
          first_name: 'Walk',
          last_name: 'In',
          email: `anonymous+walkin${useSettingStore().venue_id}+${moment().format(
            'x',
          )}@turntabletickets.com`,
          phone: '555-555-5555',
          performance: performance.id,
          tables: tableIds,
          seating_time: performance.seating_times[0].id,
          number_of_guests: numberOfGuests,
          guest_notes: '',
          internal_notes: '',
          amount,
          reader_id: this.selectedReader?.id,
          coupon_id,
        })
        if (result.error !== undefined) {
          this.setTerminalPaymentStateRetry()
        }
        return result
      } catch (error) {
        this.setTerminalPaymentStateNone()
        throw error
      }
    },
    async retrySellWalkIn(reservationId: ReservationId) {
      try {
        this.setTerminalPaymentStatePaying()
        return await api.retryReservation(reservationId, this.selectedReader?.id)
      } catch (error) {
        this.setTerminalPaymentStateRetry()
        throw error
      }
    },
    async getCoupons(): Promise<CouponRead[]> {
      const { data } = await api.getCoupons()
      return data
    },
  },
  getters: {
    isSimulatedReader(): boolean {
      return this.selectedReader?.device_type?.startsWith('simulated') || false
    },
    isPayingInTerminal(): boolean {
      return this.terminalPaymentState === TERMINAL_PAYMENT_STATE.PAYING
    },
    isRetryingPaymentInTerminal(): boolean {
      return this.terminalPaymentState === TERMINAL_PAYMENT_STATE.RETRY
    },
    hasOngoingPayment(): boolean {
      return this.isPayingInTerminal || this.isRetryingPaymentInTerminal
    },
    selectedReader(): StripeReaderRead | null {
      return this.selectedReaderId
        ? this.readers.find(reader => reader.id === this.selectedReaderId) ?? null
        : null
    },
  },
})
